import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { useOrgRole } from "@/hooks/useOrgRole";
import { RoleGate } from "@/components/RoleGate";
import NotFound from "@/pages/NotFound";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sparkles, CheckCircle2, Trash2, Clock, Gauge, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface DmnJob {
  id: string;
  group_id: string;
  insight_summary: string | null;
  insight_full: string | null;
  relevance_score: number | null;
  confidence: number | null;
  novelty_score: number | null;
  model_used: string | null;
  latency_ms: number | null;
  trigger_intent: string | null;
  seed_memory_ids: string[] | null;
  candidate_memory_ids: string[] | null;
  connections: unknown;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

const FLAG_KEYS = ["cortex.dmn", "cortex.dmn.seed", "cortex.dmn.auto_inject"] as const;

function scoreTone(v: number | null | undefined): "default" | "secondary" | "destructive" {
  const n = Number(v ?? 0);
  if (n >= 0.75) return "default";
  if (n >= 0.5) return "secondary";
  return "destructive";
}

function fmt(v: number | null | undefined) {
  if (v == null) return "—";
  return Number(v).toFixed(2);
}

function CortexDmnInboxInner() {
  const qc = useQueryClient();
  const { can } = useOrgRole();
  const canManage = can("cortex.dmn.manage");
  const { data: defaultOrgId } = useCurrentOrgId();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const orgId = selectedOrgId ?? defaultOrgId ?? null;

  const [discardOpen, setDiscardOpen] = useState<string | null>(null);
  const [discardReason, setDiscardReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [flagBusy, setFlagBusy] = useState<string | null>(null);

  async function toggleFlag(flagKey: string, nextEnabled: boolean) {
    if (!orgId || !canManage) return;
    setFlagBusy(flagKey);
    try {
      const { error } = await supabase
        .from("feature_flags")
        .upsert(
          {
            org_id: orgId,
            flag_key: flagKey,
            enabled: nextEnabled,
            rollout_pct: nextEnabled ? 100 : 0,
            description: flagKey,
          },
          { onConflict: "org_id,flag_key" },
        );
      if (error) throw new Error(error.message);
      toast.success(`${flagKey} ${nextEnabled ? "ativada" : "desativada"}`);
      qc.invalidateQueries({ queryKey: ["cortex-dmn", "flags", orgId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao alternar flag");
    } finally {
      setFlagBusy(null);
    }
  }

  const { data: availableOrgs } = useQuery({
    queryKey: ["cortex-dmn", "user-orgs"],
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("org_members")
        .select("org_id, organizations:org_id(id, name)")
        .eq("user_id", user.id);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as Array<{ organizations: { id: string; name: string } | null }>;
      return rows
        .map((r) => r.organizations)
        .filter((o): o is { id: string; name: string } => !!o)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  const { data: flags } = useQuery({
    queryKey: ["cortex-dmn", "flags", orgId],
    enabled: !!orgId,
    refetchInterval: 30_000,
    queryFn: async (): Promise<Record<string, { enabled: boolean; rollout_pct: number | null }>> => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("flag_key, enabled, rollout_pct")
        .eq("org_id", orgId!)
        .in("flag_key", FLAG_KEYS as unknown as string[]);
      if (error) throw new Error(error.message);
      const map: Record<string, { enabled: boolean; rollout_pct: number | null }> = {};
      for (const k of FLAG_KEYS) map[k] = { enabled: false, rollout_pct: 0 };
      for (const row of data ?? []) {
        map[row.flag_key] = { enabled: !!row.enabled, rollout_pct: row.rollout_pct ?? null };
      }
      return map;
    },
  });

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["cortex-dmn", "inbox", orgId],
    enabled: !!orgId,
    refetchInterval: 30_000,
    queryFn: async (): Promise<DmnJob[]> => {
      const { data, error } = await supabase
        .from("creative_incubation_jobs")
        .select("id, group_id, insight_summary, insight_full, relevance_score, confidence, novelty_score, model_used, latency_ms, trigger_intent, seed_memory_ids, candidate_memory_ids, connections, created_at, updated_at, expires_at")
        .eq("org_id", orgId!)
        .eq("state", "awaiting_admin")
        .gt("expires_at", new Date().toISOString())
        .order("updated_at", { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []) as DmnJob[];
    },
  });

  const groupIds = useMemo(() => {
    return Array.from(new Set((jobs ?? []).map((j) => j.group_id))).filter(Boolean);
  }, [jobs]);

  const { data: groupNames } = useQuery({
    queryKey: ["cortex-dmn", "groups", orgId, groupIds.join(",")],
    enabled: !!orgId && groupIds.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, name")
        .in("id", groupIds);
      if (error) throw new Error(error.message);
      const map: Record<string, string> = {};
      for (const g of data ?? []) map[g.id] = g.name;
      return map;
    },
  });

  async function handleApprove(jobId: string) {
    setBusy(jobId);
    try {
      const { data, error } = await supabase.rpc("dmn_admin_approve_job", { p_job_id: jobId });
      if (error) throw new Error(error.message);
      const result = data as { success: boolean; error?: string };
      if (!result?.success) {
        toast.error(`Não foi possível aprovar: ${result?.error ?? "erro desconhecido"}`);
      } else {
        toast.success("Insight aprovado — entrega no próximo tick.");
        qc.invalidateQueries({ queryKey: ["cortex-dmn", "inbox", orgId] });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao aprovar");
    } finally {
      setBusy(null);
    }
  }

  async function handleDiscard(jobId: string) {
    setBusy(jobId);
    try {
      const { data, error } = await supabase.rpc("dmn_admin_discard_job", {
        p_job_id: jobId,
        p_reason: discardReason.trim() || null,
      });
      if (error) throw new Error(error.message);
      const result = data as { success: boolean; error?: string };
      if (!result?.success) {
        toast.error(`Não foi possível descartar: ${result?.error ?? "erro desconhecido"}`);
      } else {
        toast.success("Insight descartado.");
        qc.invalidateQueries({ queryKey: ["cortex-dmn", "inbox", orgId] });
        setDiscardOpen(null);
        setDiscardReason("");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao descartar");
    } finally {
      setBusy(null);
    }
  }

  const masterOff = flags && !flags["cortex.dmn"]?.enabled;
  const autoInjectOff = flags && !flags["cortex.dmn.auto_inject"]?.enabled;

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold font-headline tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            DMN — Inbox de pensamentos de fundo
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Insights gerados pela rede de modo padrão (DMN) aguardando revisão antes de virarem mensagens no chat.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {FLAG_KEYS.map((k) => {
            const f = flags?.[k];
            const on = !!f?.enabled;
            return (
              <div
                key={k}
                className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 bg-card"
              >
                <Switch
                  checked={on}
                  disabled={!canManage || flagBusy === k || !orgId}
                  onCheckedChange={(v) => toggleFlag(k, v)}
                  aria-label={`Toggle ${k}`}
                />
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-mono">{k}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {on ? "on" : "off"}
                    {on && f?.rollout_pct != null && f.rollout_pct < 100 && ` · ${f.rollout_pct}%`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </header>

      {(availableOrgs?.length ?? 0) > 1 && (
        <Card>
          <CardContent className="p-3">
            <Select value={orgId ?? ""} onValueChange={(v) => setSelectedOrgId(v || null)}>
              <SelectTrigger className="w-full md:w-72">
                <SelectValue placeholder="Selecione uma org…" />
              </SelectTrigger>
              <SelectContent>
                {availableOrgs?.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {masterOff && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Flag cortex.dmn desligada</AlertTitle>
          <AlertDescription>
            Novos jobs não serão criados. A inbox abaixo mostra apenas o backlog existente.
          </AlertDescription>
        </Alert>
      )}

      {!masterOff && autoInjectOff && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>Auto-inject desligado</AlertTitle>
          <AlertDescription>
            Insights aprovados aqui serão entregues no próximo tick do deliver (≤ 5 min).
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="text-sm text-muted-foreground">Carregando…</div>
      )}

      {!isLoading && (jobs?.length ?? 0) === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Nenhum insight aguardando revisão.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(jobs ?? []).map((job) => {
          const groupName = groupNames?.[job.group_id] ?? job.group_id.slice(0, 8) + "…";
          let expiresLabel = "—";
          try {
            expiresLabel = formatDistanceToNow(new Date(job.expires_at), { addSuffix: true, locale: ptBR });
          } catch { /* ignore */ }
          return (
            <Card key={job.id} className="flex flex-col">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between gap-2">
                  <span className="truncate" title={groupName}>{groupName}</span>
                  {job.trigger_intent && (
                    <Badge variant="outline" className="text-xs">{job.trigger_intent}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col gap-2 pt-0">
                {job.insight_summary && (
                  <p className="font-medium text-sm leading-snug">{job.insight_summary}</p>
                )}
                {job.insight_full && (
                  <p className="text-muted-foreground text-sm leading-snug">{job.insight_full}</p>
                )}
                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                  <Badge variant={scoreTone(job.relevance_score)} className="gap-1 text-xs">
                    <Gauge className="h-3 w-3" />rel {fmt(job.relevance_score)}
                  </Badge>
                  <Badge variant={scoreTone(job.confidence)} className="gap-1 text-xs">
                    conf {fmt(job.confidence)}
                  </Badge>
                  <Badge variant={scoreTone(job.novelty_score)} className="gap-1 text-xs">
                    nov {fmt(job.novelty_score)}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                  {job.model_used && <span>{job.model_used}</span>}
                  {job.latency_ms != null && <span>· {job.latency_ms} ms</span>}
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />expira {expiresLabel}
                  </span>
                </div>
                {canManage && (
                  <div className="flex items-center gap-2 pt-2 mt-auto">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(job.id)}
                      disabled={busy === job.id}
                      className="gap-1"
                    >
                      <CheckCircle2 className="h-4 w-4" />Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setDiscardOpen(job.id); setDiscardReason(""); }}
                      disabled={busy === job.id}
                      className="gap-1"
                    >
                      <Trash2 className="h-4 w-4" />Descartar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!discardOpen} onOpenChange={(o) => { if (!o) { setDiscardOpen(null); setDiscardReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Descartar insight</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Opcional: descreva por que este insight não deve ser entregue.
            </p>
            <Textarea
              value={discardReason}
              onChange={(e) => setDiscardReason(e.target.value)}
              placeholder="Ex.: irrelevante para o contexto, repete conteúdo já enviado…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDiscardOpen(null); setDiscardReason(""); }}
              disabled={!!busy}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => discardOpen && handleDiscard(discardOpen)}
              disabled={!!busy}
            >
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CortexDmnInbox() {
  return (
    <RoleGate permission="cortex.dmn.read" fallback={<NotFound />}>
      <CortexDmnInboxInner />
    </RoleGate>
  );
}
